import { useState, useCallback, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { appColors } from '@base/useTheme';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { FslEditor } from './FslEditor';
import { analyzeFsl, compileFsl, generateFsl, lintFsl } from './fslApi';
import { FslCompileResponse, FslFinding } from './types';

const EXAMPLE_FSL = `# Farseer Model DSL - Example
# See the DSL specification for full syntax reference

@defaults
  @type number
  @rollup sum

# --- Dimension links ---

link SKU -> Product
link Product -> Category
link Month -> Quarter
link Quarter -> Year

# --- Variables ---

Units { Product, Month, Version }
  @desc "Units sold"

Price { Product, Version }
  @format currency(EUR)
  @desc "Unit price"

Revenue { Product, Month, Version } = Units * Price
  @format currency(EUR)
  @folder "P&L/Revenue"
  @desc "Recognized revenue"

"Direct Costs" { Product, Month, Version }
  @format currency(EUR)
  @folder "P&L/Costs"

"Gross Profit" { Product, Month, Version } = Revenue - "Direct Costs"
  @format currency(EUR)
  @folder "P&L"
`;

const SEVERITY_ORDER: Record<string, number> = { error: 0, warning: 1, info: 2 };

/**
 * Farseer Model DSL experiment tab.
 *
 * Provides a CodeMirror editor with FSL syntax highlighting and
 * backend integration for linting and compilation.
 *
 * @experimental Part of Farseer Labs. May be changed or removed without notice.
 */
export function FslExperiment() {
    const [source, setSource] = useState(EXAMPLE_FSL);
    const [findings, setFindings] = useState<FslFinding[]>([]);
    const [compileResult, setCompileResult] = useState<FslCompileResponse | null>(null);
    const [isCompiling, setIsCompiling] = useState(false);
    const [isLoadingModel, setIsLoadingModel] = useState(false);
    const [activeResultTab, setActiveResultTab] = useState<'model' | 'diagnostics' | 'ai'>('model');
    const lintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // AI analysis state
    const [aiContent, setAiContent] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const aiScrollRef = useRef<HTMLDivElement | null>(null);

    const handleLoadModel = useCallback(async () => {
        setIsLoadingModel(true);
        try {
            const fsl = await generateFsl();
            setSource(fsl);
            setFindings([]);
            setCompileResult(null);
        } catch (e) {
            setFindings([
                {
                    from: 0,
                    to: 0,
                    line: 1,
                    column: 1,
                    severity: 'error',
                    code: 'GENERATE_FAILED',
                    message: e instanceof Error ? e.message : 'Failed to load model',
                },
            ]);
            setActiveResultTab('diagnostics');
        } finally {
            setIsLoadingModel(false);
        }
    }, []);

    const handleChange = useCallback((value: string) => {
        setSource(value);

        // Debounced lint
        if (lintTimer.current) clearTimeout(lintTimer.current);
        lintTimer.current = setTimeout(async () => {
            try {
                const result = await lintFsl(value);
                setFindings(result);
            } catch {
                // Backend not available yet - ignore
            }
        }, 500);
    }, []);

    const handleCompile = useCallback(async () => {
        setIsCompiling(true);
        try {
            const result = await compileFsl(source);
            setCompileResult(result);
            setFindings(result.findings);
            setActiveResultTab(result.findings.some((f) => f.severity === 'error') ? 'diagnostics' : 'model');
        } catch {
            // Backend not available yet
            setCompileResult(null);
            setFindings([
                {
                    from: 0,
                    to: 0,
                    line: 1,
                    column: 1,
                    severity: 'info',
                    code: 'N/A',
                    message: 'Backend not available. Compile endpoint not yet implemented.',
                },
            ]);
            setActiveResultTab('diagnostics');
        } finally {
            setIsCompiling(false);
        }
    }, [source]);

    const handleAnalyze = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
        setAiContent('');
        setIsAnalyzing(true);
        setActiveResultTab('ai');

        abortRef.current = analyzeFsl(
            source,
            {
                onToken: (token) => setAiContent((prev) => prev + token),
                onComplete: () => setIsAnalyzing(false),
                onError: (error) => {
                    setAiContent((prev) => prev + `\n\n**Error:** ${error}`);
                    setIsAnalyzing(false);
                },
            },
            aiPrompt || undefined
        );
    }, [source, aiPrompt]);

    const handleStopAnalysis = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
        setIsAnalyzing(false);
    }, []);

    // Auto-scroll AI output during streaming
    useEffect(() => {
        if (isAnalyzing && aiScrollRef.current) {
            aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
        }
    }, [aiContent, isAnalyzing]);

    const sortedFindings = [...findings].sort(
        (a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9)
    );

    const errorCount = findings.filter((f) => f.severity === 'error').length;
    const warnCount = findings.filter((f) => f.severity === 'warning').length;
    const infoCount = findings.filter((f) => f.severity === 'info').length;

    return (
        <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <FlexCol $gap={12} style={{ flex: 1, minHeight: 0 }}>
                {/* Header */}
                <FlexRow $gap={8} style={{ alignItems: 'center', flexShrink: 0 }}>
                    <h3 style={{ margin: 0 }}>Model DSL</h3>
                    <span style={{ color: '#6b7280', fontSize: 12 }}>
                        Write Farseer models as code. Press Ctrl+Enter to compile.
                    </span>
                </FlexRow>

                {/* Main content */}
                <FlexRow $gap={12} style={{ flex: 1, minHeight: 0 }}>
                    {/* Editor pane */}
                    <FlexCol style={{ flex: 3, minHeight: 0 }}>
                        <FlexRow $gap={8} style={{ marginBottom: 8, alignItems: 'center', flexShrink: 0 }}>
                            <button
                                onClick={handleCompile}
                                disabled={isCompiling}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 4,
                                    border: '1px solid #d1d5db',
                                    background: isCompiling ? '#f3f4f6' : '#fff',
                                    cursor: isCompiling ? 'wait' : 'pointer',
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}
                            >
                                {isCompiling ? 'Compiling...' : 'Compile'}
                            </button>
                            <button
                                onClick={handleLoadModel}
                                disabled={isLoadingModel}
                                title="Load the current model as FSL"
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 4,
                                    border: '1px solid #d1d5db',
                                    background: isLoadingModel ? '#f3f4f6' : '#fff',
                                    cursor: isLoadingModel ? 'wait' : 'pointer',
                                    fontSize: 12,
                                    fontWeight: 500,
                                }}
                            >
                                {isLoadingModel ? 'Loading...' : 'Load Model'}
                            </button>
                            {findings.length > 0 && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                    {errorCount > 0 && (
                                        <span style={{ color: '#ef4444' }}>
                                            {errorCount} error{errorCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {errorCount > 0 && warnCount > 0 && ', '}
                                    {warnCount > 0 && (
                                        <span style={{ color: '#f59e0b' }}>
                                            {warnCount} warning{warnCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {(errorCount > 0 || warnCount > 0) && infoCount > 0 && ', '}
                                    {infoCount > 0 && <span style={{ color: '#3b82f6' }}>{infoCount} info</span>}
                                </span>
                            )}
                        </FlexRow>
                        <FslEditor
                            value={source}
                            onChange={handleChange}
                            onCompile={handleCompile}
                            findings={findings}
                        />
                    </FlexCol>

                    {/* Results pane */}
                    <FlexCol style={{ flex: 2, minHeight: 0, overflow: 'hidden' }}>
                        <FlexRow $gap={4} style={{ marginBottom: 8, flexShrink: 0 }}>
                            <TabButton active={activeResultTab === 'model'} onClick={() => setActiveResultTab('model')}>
                                Model
                            </TabButton>
                            <TabButton
                                active={activeResultTab === 'diagnostics'}
                                onClick={() => setActiveResultTab('diagnostics')}
                            >
                                Diagnostics
                                {findings.length > 0 && ` (${findings.length})`}
                            </TabButton>
                            <TabButton active={activeResultTab === 'ai'} onClick={() => setActiveResultTab('ai')}>
                                AI
                            </TabButton>
                        </FlexRow>

                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                border: '1px solid #e5e7eb',
                                borderRadius: 6,
                                background: '#fafafa',
                                padding: 12,
                                fontSize: 12,
                                fontFamily: 'Fira Code, monospace',
                            }}
                        >
                            {activeResultTab === 'model' &&
                                (compileResult?.model ? (
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {JSON.stringify(compileResult.model, null, 2)}
                                    </pre>
                                ) : (
                                    <span style={{ color: '#9ca3af', fontFamily: 'inherit' }}>
                                        Click "Compile" to see the parsed model.
                                    </span>
                                ))}
                            {activeResultTab === 'diagnostics' &&
                                (sortedFindings.length > 0 ? (
                                    <FlexCol $gap={6}>
                                        {sortedFindings.map((f, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: 4,
                                                    background:
                                                        f.severity === 'error'
                                                            ? '#fef2f2'
                                                            : f.severity === 'warning'
                                                            ? '#fffbeb'
                                                            : '#eff6ff',
                                                    border: `1px solid ${
                                                        f.severity === 'error'
                                                            ? '#fecaca'
                                                            : f.severity === 'warning'
                                                            ? '#fde68a'
                                                            : '#bfdbfe'
                                                    }`,
                                                    fontSize: 11,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        color:
                                                            f.severity === 'error'
                                                                ? '#ef4444'
                                                                : f.severity === 'warning'
                                                                ? '#d97706'
                                                                : '#3b82f6',
                                                    }}
                                                >
                                                    [{f.code}]
                                                </span>{' '}
                                                <span style={{ color: '#6b7280' }}>
                                                    L{f.line}:{f.column}
                                                </span>{' '}
                                                {f.message}
                                            </div>
                                        ))}
                                    </FlexCol>
                                ) : (
                                    <span style={{ color: '#9ca3af', fontFamily: 'inherit' }}>No diagnostics.</span>
                                ))}
                            {activeResultTab === 'ai' && (
                                <FlexCol $gap={8} style={{ height: '100%' }}>
                                    <FlexRow $gap={6} style={{ flexShrink: 0 }}>
                                        <input
                                            type="text"
                                            placeholder="Ask about your model (optional)"
                                            value={aiPrompt}
                                            onChange={(e) => setAiPrompt(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !isAnalyzing) handleAnalyze();
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                border: `1px solid ${appColors.neutral.w300}`,
                                                fontSize: 12,
                                                fontFamily: 'inherit',
                                            }}
                                        />
                                        {isAnalyzing ? (
                                            <button
                                                onClick={handleStopAnalysis}
                                                style={{
                                                    padding: '4px 12px',
                                                    borderRadius: 4,
                                                    border: `1px solid ${appColors.error.w300}`,
                                                    background: appColors.error.w50,
                                                    color: appColors.error.w700,
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Stop
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleAnalyze}
                                                style={{
                                                    padding: '4px 12px',
                                                    borderRadius: 4,
                                                    border: `1px solid ${appColors.primary.w300}`,
                                                    background: appColors.primary.w50,
                                                    color: appColors.primary.w700,
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Analyze
                                            </button>
                                        )}
                                    </FlexRow>
                                    <div
                                        ref={aiScrollRef}
                                        style={{
                                            flex: 1,
                                            overflow: 'auto',
                                            fontSize: 13,
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {aiContent ? (
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiContent}</ReactMarkdown>
                                        ) : (
                                            <span style={{ color: appColors.neutral.w400, fontFamily: 'inherit' }}>
                                                Click &quot;Analyze&quot; to get AI insights about your model.
                                            </span>
                                        )}
                                        {isAnalyzing && <span style={{ color: appColors.neutral.w400 }}>▍</span>}
                                    </div>
                                </FlexCol>
                            )}
                        </div>
                    </FlexCol>
                </FlexRow>
            </FlexCol>
        </div>
    );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            style={{
                padding: '4px 10px',
                borderRadius: 4,
                border: 'none',
                background: active ? '#e5e7eb' : 'transparent',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: active ? 600 : 400,
                color: active ? '#111827' : '#6b7280',
            }}
        >
            {children}
        </button>
    );
}
